// Logique d'entitlement d'abonnement — fonctions PURES et testables.
//
// C'est la SOURCE UNIQUE DE VÉRITÉ d'accès premium côté frontend, extraite de
// useSubscription.ts pour être testable sans monter les listeners Firebase.
// Elle est alignée avec le hard gate backend `validatePremiumAccess`
// (functions/src/shared/premiumGuard.ts).
//
// Règle d'or : un abonné dont le paiement n'est PAS à jour (past_due / unpaid /
// canceled / incomplete) NE bénéficie PAS des features premium — il retombe au
// plan gratuit, même si son `tier` stocké reste `pro_monthly`.

import {
  LicenseInfo,
  LicenseOverride,
  SubscriptionFeatures,
  SubscriptionStatus,
  SubscriptionTier,
  SUBSCRIPTION_PLANS,
} from '../types/subscription.types';

/** Rôles internes ayant un accès pro complet sans abonnement Stripe. */
export const ADMIN_ROLES = ['admin', 'support', 'finance', 'tech'] as const;

/** Statuts Stripe qui donnent réellement accès aux features premium. */
const ENTITLING_STATUSES: ReadonlySet<string> = new Set(['active', 'trialing']);

export interface LicenseInputs {
  userRole: string | null;
  licenseOverride: LicenseOverride | null;
  tier: SubscriptionTier;
  status: SubscriptionStatus | 'loading';
  hasStripeCustomerId: boolean;
}

/**
 * Calcule la licence effective (Admin role > Override admin > Stripe).
 *
 * `isActive` est LE booléen d'entitlement : toute décision d'accès premium
 * (paywall, FeatureGate, quotas, autopilote) doit en dériver, jamais du `tier`
 * brut. Seuls `active` et `trialing` (ou admin/override) rendent `isActive`
 * vrai — `past_due`, `unpaid`, `canceled`, `incomplete` retombent à `false`.
 */
export function computeLicense({
  userRole,
  licenseOverride,
  tier,
  status,
  hasStripeCustomerId,
}: LicenseInputs): LicenseInfo {
  // 1. PRIORITÉ MAXIMALE : rôle admin (admin/support/finance/tech)
  if (userRole && (ADMIN_ROLES as readonly string[]).includes(userRole)) {
    return {
      isActive: true,
      source: 'admin',
      tierEffective: 'premium_monthly', // Admins = accès complet, autopilote inclus
      statusEffective: 'active',
    };
  }

  // 2. Override (achat one-time 49 CHF OU grant admin manuel). Le webhook
  //    grantOneTimeAccess pose `note: 'one_time_*'` → on distingue l'achat
  //    payant du grant admin pour que l'affichage montre « Accès complet » et
  //    NON « Licence administrateur / Pro Mensuel » à un client payant
  //    (incident blazesun). tierEffective='one_time' a les mêmes features que
  //    pro_monthly (SUBSCRIPTION_PLANS.one_time = CVs illimités).
  if (licenseOverride?.enabled) {
    const isOneTime = (licenseOverride.note || '').startsWith('one_time');
    return {
      isActive: true,
      source: isOneTime ? 'one_time' : 'admin',
      tierEffective: isOneTime ? 'one_time' : licenseOverride.tier,
      statusEffective: 'active', // Override = toujours actif
    };
  }

  const statusEntitles = ENTITLING_STATUSES.has(status);

  // 3. Abonnement Stripe normal (vrai compte Stripe + statut valide)
  if (statusEntitles && tier !== 'free' && hasStripeCustomerId) {
    return {
      isActive: true,
      source: 'stripe',
      tierEffective: tier,
      statusEffective: status as SubscriptionStatus,
    };
  }

  // 4. Abonnement actif sans compte Stripe (cas manuel/legacy) → traité admin
  if (statusEntitles && tier !== 'free' && !hasStripeCustomerId) {
    return {
      isActive: true,
      source: 'admin',
      tierEffective: tier,
      statusEffective: status as SubscriptionStatus,
    };
  }

  // 5. Pas de licence active — couvre past_due / unpaid / canceled /
  //    incomplete / pas d'abonnement : retour au plan gratuit.
  return {
    isActive: false,
    source: 'none',
    tierEffective: 'free',
    statusEffective: 'inactive',
  };
}

/**
 * Calcule les features à partir du TIER EFFECTIF de la licence (≠ tier brut).
 * Une licence inactive → features du plan gratuit. Les admins gardent un quota
 * CV illimité quoi qu'il arrive.
 */
export function computeFeatures(license: LicenseInfo, isAdmin: boolean): SubscriptionFeatures {
  const effectiveTier: SubscriptionTier = license.isActive ? license.tierEffective : 'free';

  const plan = SUBSCRIPTION_PLANS[effectiveTier as keyof typeof SUBSCRIPTION_PLANS];
  const baseFeatures = plan ? plan.features : SUBSCRIPTION_PLANS.free.features;

  if (isAdmin) {
    return {
      ...baseFeatures,
      maxCVs: -1,
      hasUnlimitedCVs: true,
    };
  }

  return baseFeatures;
}
